/**
 * The review bar (docs/ux/casefile.md): the state chip and sentence on the
 * left; on the right the blocking sentence with Complete review, or the
 * rebuild's progress, or the failure with Rebuild again, or the built
 * sentence with the reviewed Sorted.pdf. Complete review is the tab's only
 * ink primary while the blocking list is empty; refused, it stays focusable
 * with its sentence as its description and never fires.
 */

import { cn } from 'cn';
import { FlagIcon } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import type { SortReviewItem, SortRunHead } from '@/app/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { dateTime, longDate } from '@/lib/format';
import { OpenPdfLink } from './ReplayPanel';
import { sortedPdfUrl } from './useSort';

export type ReviewPhase = 'review' | 'rebuilding' | 'rebuild_failed' | 'built';

export function phaseOf(head: SortRunHead): ReviewPhase {
  if (!head.completedAt) return 'review';
  if (head.reviewedPointer && head.rebuild?.state !== 'queued' && head.rebuild?.state !== 'running') return 'built';
  if (head.rebuild?.state === 'failed') return 'rebuild_failed';
  return 'rebuilding';
}

const TONE: Record<string, string> = {
  review_required: 'bg-beam-cream-deep text-foreground border-beam-line',
  in_review: 'bg-beam-cream-deep text-foreground border-beam-line',
  mine: 'bg-beam-harbour/55 text-beam-harbour-ink border-transparent',
  escalated: 'bg-beam-warning-bg text-beam-warning border-transparent',
  complete: 'bg-beam-sage/55 text-beam-sage-ink border-transparent',
  rebuild_failed: 'bg-beam-error-bg text-beam-error border-transparent',
};

export { StateChip, type ChipTone } from '@/components/matter/StateChip';

export function ReviewStateChip({ head, phase, mine }: { head: SortRunHead; phase: ReviewPhase; mine: boolean }) {
  const state = phase === 'rebuild_failed' ? 'rebuild_failed' : head.state;
  const label = phase === 'rebuild_failed' ? 'Rebuild failed' : phase === 'rebuilding' ? 'Complete: rebuilding' : head.state === 'review_required' ? 'Review required' : head.state === 'in_review' ? 'In review' : head.state === 'escalated' ? 'Escalated' : 'Complete';
  const tone = state === 'in_review' && mine ? TONE.mine : TONE[state] ?? TONE.review_required;
  return (
    <Badge variant="outline" data-slot="review-state" data-review-state={state} className={cn('h-7 gap-1 rounded-full px-3 text-[13px] font-medium', tone)}>
      {state === 'escalated' ? <FlagIcon aria-hidden="true" className="size-3.5" /> : null}
      {label}
    </Badge>
  );
}

export type ReviewCounts = { total: number; decided: number; undecided: number; escalated: number; accepted: number; corrected: number; additional: number };

export function countsOf(items: SortReviewItem[]): ReviewCounts {
  const flagged = items.filter((i) => i.category !== 'additional');
  return {
    total: flagged.length,
    decided: flagged.filter((i) => i.decision !== 'unresolved').length,
    undecided: flagged.filter((i) => i.decision === 'unresolved').length,
    escalated: items.filter((i) => i.decision === 'escalated').length,
    accepted: flagged.filter((i) => i.decision === 'accepted').length,
    corrected: flagged.filter((i) => i.decision === 'corrected').length,
    additional: items.filter((i) => i.category === 'additional' && i.decision === 'corrected').length,
  };
}

export function blockingSentence(counts: ReviewCounts): string {
  const blocking = counts.undecided + counts.escalated;
  if (blocking === 0) return counts.total === 0 ? '0 items to decide. Complete the review to build the reviewed casefile.' : 'Every item is decided. Complete the review to build the reviewed casefile.';
  const parts: string[] = [];
  if (counts.undecided > 0) parts.push(`${counts.undecided} undecided`);
  if (counts.escalated > 0) parts.push(`${counts.escalated} escalated`);
  return `${blocking} ${blocking === 1 ? 'item blocks' : 'items block'} completion: ${parts.join(', ')}.`;
}

export function stateSentence(head: SortRunHead, counts: ReviewCounts, myName: string | null): string {
  if (head.state === 'review_required' || !head.startedAt) return 'Review required. No decisions yet.';
  const who = head.reviewerName ?? head.reviewer ?? 'someone';
  const since = new Date(head.startedAt);
  const time = since.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false });
  const by = myName ? 'you' : who;
  return `In review by ${by} since ${time}. ${counts.decided} of ${counts.total} decided.`;
}

export function completedSentence(head: SortRunHead, counts: ReviewCounts): string {
  const at = head.completedAt ? new Date(head.completedAt) : null;
  const who = head.completedByName ?? head.completedBy ?? 'someone';
  const when = at ? `${longDate(at.toISOString())} at ${at.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false })}` : '';
  const extra = counts.additional > 0 ? ` ${counts.additional} additional ${counts.additional === 1 ? 'correction' : 'corrections'}.` : '';
  return `Completed by ${who} on ${when}. ${counts.total} ${counts.total === 1 ? 'item' : 'items'}: ${counts.accepted} accepted, ${counts.corrected} corrected.${extra}`;
}

export function ReviewBar({ matterRef, head, counts, phase, mine, myName, offline, phone, completing, refusedReasons, refusedAt, onComplete, onRebuildAgain, rebuilding, statusRef, pollStalled, onCheckNow }: {
  matterRef: string;
  head: SortRunHead;
  counts: ReviewCounts;
  phase: ReviewPhase;
  mine: boolean;
  myName: string | null;
  offline: boolean;
  /** Two rebuild polls have failed; the bar says so and offers a manual check. */
  pollStalled?: boolean;
  onCheckNow?: () => void;
  /** The phone reads; it never completes. */
  phone: boolean;
  completing: boolean;
  refusedReasons: string[] | null;
  refusedAt: string | null;
  onComplete: () => Promise<boolean>;
  onRebuildAgain: () => void;
  rebuilding: boolean;
  statusRef?: React.Ref<HTMLParagraphElement>;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogError, setDialogError] = useState(false);
  // The attempt made from this dialog was refused: the confirm goes inert and reads the reasons; a fresh opening starts clean.
  const [dialogRefused, setDialogRefused] = useState(false);
  // A second click before React re-renders the disabled button must not send a second completion.
  const inFlight = useRef(false);
  const blocking = counts.undecided + counts.escalated;
  const canComplete = blocking === 0;

  async function confirm() {
    if (inFlight.current) return;
    inFlight.current = true;
    setDialogError(false);
    try {
      const ok = await onComplete();
      if (ok) { setConfirmOpen(false); return; }
      // The mutation's callbacks have set refusedReasons when it was a 422; anything else is a plain failure.
      setDialogRefused(true);
      setDialogError(true);
    } finally {
      inFlight.current = false;
    }
  }

  let right: ReactNode;
  if (phase === 'review') {
    right = (
      <div className="flex flex-wrap items-center justify-end gap-3">
        <span id="review-blocking" className={cn('text-[13px]', canComplete ? 'text-beam-text-soft' : 'text-beam-text-muted')}>{offline ? `${blockingSentence(counts)} Actions are paused while offline.` : blockingSentence(counts)}</span>
        {phone ? null : (
          <Button
            variant={canComplete ? 'default' : 'outline'}
            size="lg"
            data-primary={canComplete && !offline ? 'true' : undefined}
            aria-disabled={canComplete && !completing ? undefined : 'true'}
            aria-describedby="review-blocking"
            disabled={offline}
            className={cn('h-9', !canComplete && 'cursor-default text-beam-text-muted hover:bg-beam-paper')}
            onClick={() => { if (canComplete && !completing) { setDialogError(false); setDialogRefused(false); setConfirmOpen(true); } }}
          >
            {completing ? 'Completing' : 'Complete review'}
          </Button>
        )}
      </div>
    );
  } else if (phase === 'rebuilding') {
    right = (
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <p ref={statusRef} id="review-status" role="status" tabIndex={-1} className="text-[13px] text-beam-text-soft outline-none">
          Rebuilding the reviewed casefile: applying {counts.corrected + counts.additional} {counts.corrected + counts.additional === 1 ? 'correction' : 'corrections'}, re-running the sorter's invariants, writing the reviewed manifest and Sorted.pdf.
          {pollStalled ? <> Checking the rebuild is delayed. Retrying. {onCheckNow ? <button type="button" className="text-beam-signature underline-offset-4 hover:underline" onClick={onCheckNow}>Check now</button> : null}</> : null}
        </p>
        <div aria-hidden="true" className="relative h-1 w-full overflow-hidden rounded-full bg-beam-cream-deep">
          <div className="absolute inset-y-0 w-1/3 rounded-full bg-beam-ink motion-safe:animate-[beam-indeterminate_1.4s_ease-in-out_infinite] motion-reduce:w-full motion-reduce:bg-beam-ink/40" />
        </div>
      </div>
    );
  } else if (phase === 'rebuild_failed') {
    right = (
      <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-l-[3px] border-beam-line border-l-beam-error bg-beam-paper px-3 py-2 text-[13px]">
        <span>The reviewed casefile could not be built. Your decisions are kept and the machine result is unchanged.</span>
        {phone ? null : <Button variant="outline" size="lg" className="h-9" disabled={offline} aria-disabled={rebuilding ? 'true' : undefined} onClick={() => { if (!rebuilding) onRebuildAgain(); }}>{offline ? 'Paused while offline' : rebuilding ? 'Starting' : 'Rebuild again'}</Button>}
      </div>
    );
  } else {
    right = (
      <div className="flex flex-wrap items-center justify-end gap-3">
        {head.reviewedPointer ? <span className="text-[13px] text-beam-text-muted">Reviewed casefile built at {dateTime(head.reviewedPointer.builtAt).split(', ')[1]}.</span> : null}
        <OpenPdfLink variant="outline" href={sortedPdfUrl(matterRef, head.runId, 'reviewed')}>Open reviewed Sorted.pdf</OpenPdfLink>
      </div>
    );
  }

  return (
    <section aria-label="Review" data-slot="review-bar" className="flex flex-col gap-2 rounded-xl border border-beam-line bg-beam-paper px-4 py-2">
      <div className="flex min-h-7 flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <ReviewStateChip head={head} phase={phase} mine={mine} />
          <span className="text-[13px]">{phase === 'review' ? stateSentence(head, counts, mine ? myName : null) : completedSentence(head, counts)}</span>
        </div>
        <div className="ml-auto flex min-w-0 flex-1 justify-end">{right}</div>
      </div>
      {refusedReasons && phase === 'review' ? (
        <Alert className="border-beam-warning-bg bg-beam-warning-bg text-beam-warning">
          <AlertTitle>Completion was refused{refusedAt ? ` at ${refusedAt}` : ''}: {refusedReasons.join('; ')}.</AlertTitle>
          <AlertDescription className="text-beam-warning">Fix the items named and try again.</AlertDescription>
        </Alert>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!completing) setConfirmOpen(o); }}>
        <AlertDialogContent className="sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Complete the review?</AlertDialogTitle>
            <AlertDialogDescription>
              {counts.total} {counts.total === 1 ? 'item' : 'items'} decided: {counts.accepted} accepted, {counts.corrected} corrected.{counts.additional > 0 ? ` ${counts.additional} additional ${counts.additional === 1 ? 'correction' : 'corrections'}.` : ''} The reviewed manifest and Sorted.pdf will be rebuilt with these corrections and billable pages recomputed. Decisions lock now and cannot be changed after completion. The machine result is kept as it is.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {refusedReasons && confirmOpen && dialogRefused && refusedReasons.length > 0 ? (
            <div id="review-confirm-reasons" role="alert" className="rounded-lg border border-l-[3px] border-beam-line border-l-beam-warning bg-beam-paper px-3 py-2 text-[13px]">
              <p className="font-medium">Completion was refused:</p>
              <ul className="mt-1 list-disc pl-5">{refusedReasons.map((r) => <li key={r}>{r}</li>)}</ul>
            </div>
          ) : dialogError ? (
            <div role="alert" className="rounded-lg border border-l-[3px] border-beam-line border-l-beam-error bg-beam-paper px-3 py-2 text-[13px]">The review could not be completed. Try again.</div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus disabled={completing}>Keep reviewing</AlertDialogCancel>
            {/* After a refusal the confirm stays visible but inert and described by the reasons: the fix is in the queue, not in this dialog. */}
            <AlertDialogAction aria-disabled={completing || (dialogRefused && refusedReasons && refusedReasons.length > 0) ? 'true' : undefined} aria-describedby={dialogRefused && refusedReasons && refusedReasons.length > 0 ? 'review-confirm-reasons' : undefined} className={dialogRefused && refusedReasons && refusedReasons.length > 0 ? 'cursor-default opacity-60' : undefined} onClick={(e) => { e.preventDefault(); if (!(dialogRefused && refusedReasons && refusedReasons.length > 0)) void confirm(); }}>{completing ? 'Completing' : 'Complete review'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
